import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listAllTrainingEvalScenarios,
  type TrainingEvalMetricsView,
  type TrainingEvalRunSummaryView,
  type TrainingEvalRunView,
  type TrainingEvalScenarioView,
  type TrainingEvalSuccessTermsView,
} from "@/api/trainingEval";
import type { LoadState } from "@/components/dataset";
import { createLogger } from "@/logging";
import { resolveEvalLoadErrorMessage } from "@/modules/evaluation/public";
import { resolveEvalScenario, resolveRunExecution } from "../evalRunScenarioModel";
import { loadEvalRun, loadEvalRunMetrics, loadEvalRunSuccessTerms } from "../evalRunDataCache";
import {
  buildCompareRecord,
  buildCompareRuns,
  type CompareRun,
  type EvalCompareRecord,
} from "./evalCompareModel";

import { useEvalImageMetadata, type EvalImageMetadataStates } from "../useEvalImageMetadata";
import { useEvalBaseline } from "../useEvalBaseline";
import type { EvalBaselineState } from "../evalBaselineModel";

const NO_SCENARIOS: readonly TrainingEvalScenarioView[] = [];

const logger = createLogger("page/evaluationResults");

type CompareRunLoad = {
  detail: LoadState<TrainingEvalRunView>;
  metrics: LoadState<TrainingEvalMetricsView>;
  terms: LoadState<TrainingEvalSuccessTermsView>;
};

type LoadField = keyof CompareRunLoad;

export type EvalCompareController = {
  compareRuns: CompareRun[];
  loads: Readonly<Record<string, CompareRunLoad>>;
  metadataStates: EvalImageMetadataStates;
  baseline: EvalBaselineState;
  // 대기 중인 요청이 하나라도 있으면 true 다. 헤더에 로딩 표시를 추가한다.
  loading: boolean;
  // 요청이 하나라도 실패하면 true 다. 그 셀은 N/A 로 두고 재시도를 제공한다.
  loadFailed: boolean;
  termsFailed: boolean;
  // seed 를 고려하는 병합 계산도 모든 상세 요청이 끝날 때까지 기다린다.
  mergeReady: boolean;
  retry: () => void;
  retryMetadata: () => void;
};

function loadingLoads(
  runs: readonly TrainingEvalRunSummaryView[],
  loadMetrics: boolean,
): Record<string, CompareRunLoad> {
  return Object.fromEntries(
    runs.map((run) => [
      run.id,
      {
        detail: { status: "loading" },
        metrics: { status: loadMetrics ? "loading" : "idle" },
        terms: { status: "loading" },
      } satisfies CompareRunLoad,
    ]),
  );
}

function dataOf<T>(state: LoadState<T> | undefined): T | null {
  return state?.status === "success" ? state.data : null;
}

// run 별 캐시 항목이다. run 하나의 요청이 끝날 때마다 모든 run 의 record 를 다시 만들지 않게 한다.
// buildCompareRecord 는 순수 함수이고, 입력(응답 객체·scenario 목록 원소)은 모두 참조가 안정적이다.
// 그래서 참조 비교만으로 재사용 여부를 판정할 수 있다.
type RecordCacheEntry = {
  summary: TrainingEvalRunSummaryView;
  detail: TrainingEvalRunView | null;
  metrics: TrainingEvalMetricsView | null;
  terms: TrainingEvalSuccessTermsView | null;
  scenario: TrainingEvalScenarioView | null;
  record: EvalCompareRecord;
};

/**
 * 선택한 모든 run 의 상세, 선택적 metrics, success terms 를 병렬로 로드한다.
 * succeeded run 만 선택할 수 있으므로 polling 은 필요 없다. 응답은 도착하는 대로 적용한다.
 * 창이 최소화된 동안에는 로드를 미룬다.
 */
export function useEvalCompareController(
  runs: readonly TrainingEvalRunSummaryView[],
  slotById: Readonly<Record<string, number>>,
  enabled: boolean,
  loadMetrics: boolean = true,
): EvalCompareController {
  const [loads, setLoads] = useState<Record<string, CompareRunLoad>>({});
  const [scenariosState, setScenariosState] = useState<
    LoadState<readonly TrainingEvalScenarioView[]>
  >({
    status: "idle",
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const baseline = useEvalBaseline(
    runs,
    scenariosState.status === "success" ? scenariosState.data : NO_SCENARIOS,
    enabled,
    refreshKey,
  );

  // 이 요청 묶음의 활성 여부는 호출자가 정한다. cleanup 뒤에 늦게 온 응답은 무시한다.
  useEffect(() => {
    if (!enabled) return;
    let disposed = false;

    setLoads(loadingLoads(runs, loadMetrics));
    setScenariosState({ status: "loading" });

    // loadingLoads 는 요청이 끝나기 전에 모든 run 키를 초기화한다.
    const settle = <T>(id: string, field: LoadField, request: Promise<T>): void => {
      void request
        .then((data) => {
          if (disposed) return;
          setLoads((prev) => ({
            ...prev,
            [id]: { ...prev[id], [field]: { status: "success", data } },
          }));
        })
        .catch((error: unknown) => {
          if (disposed) return;
          logger.error(`compare load failed: ${field} ${id}`, error);
          setLoads((prev) => ({
            ...prev,
            [id]: {
              ...prev[id],
              [field]: { status: "error", message: resolveEvalLoadErrorMessage(error) },
            },
          }));
        });
    };

    // 요청한 run 별 데이터와 공용 scenario 목록을 한 번에 요청한다. 로더는 공용 eval-run 캐시다.
    // 그래서 이미 상세 창으로 연 run 은 요청 없이 값을 받는다. 최소화 뒤 복원할 때는 모든 run 이
    // 요청 없이 값을 받는다.
    for (const run of runs) {
      settle(run.id, "detail", loadEvalRun(run.id));
      if (loadMetrics) {
        settle(run.id, "metrics", loadEvalRunMetrics(run.id));
      }
      settle(run.id, "terms", loadEvalRunSuccessTerms(run.id));
    }
    void listAllTrainingEvalScenarios()
      .then((data) => {
        if (!disposed) setScenariosState({ status: "success", data });
      })
      .catch((error: unknown) => {
        if (disposed) return;
        logger.error("listAllTrainingEvalScenarios failed", error);
        setScenariosState({ status: "error", message: resolveEvalLoadErrorMessage(error) });
      });

    return () => {
      disposed = true;
    };
  }, [enabled, loadMetrics, refreshKey, runs]);

  // 응답 하나가 끝날 때마다 이 memo 가 다시 실행된다. run 수가 크면(정책 비교) 매번 모든 run 의
  // record 를 새로 만드는 비용이 응답 수 × run 수로 커진다. 그래서 입력이 같은 run 은 이전
  // record 를 재사용하고, 바뀐 run 만 다시 만든다.
  const recordCacheRef = useRef(new Map<string, RecordCacheEntry>());
  const compareRuns = useMemo(() => {
    const scenarios = scenariosState.status === "success" ? scenariosState.data : [];
    const cache = recordCacheRef.current;
    const records = runs.map((summary) => {
      const load = loads[summary.id];
      const detail = dataOf(load?.detail);
      const metrics = dataOf(load?.metrics);
      const terms = dataOf(load?.terms);
      const scenario = resolveEvalScenario(scenarios, detail?.scenario ?? summary.scenario);
      const cached = cache.get(summary.id);
      if (
        cached !== undefined &&
        cached.summary === summary &&
        cached.detail === detail &&
        cached.metrics === metrics &&
        cached.terms === terms &&
        cached.scenario === scenario
      ) {
        return cached.record;
      }
      const record = buildCompareRecord({ summary, detail, metrics, terms, scenario });
      cache.set(summary.id, { summary, detail, metrics, terms, scenario, record });
      return record;
    });
    return buildCompareRuns(records, slotById);
  }, [runs, loads, scenariosState, slotById]);

  const metadataTags = loadMetrics
    ? compareRuns.flatMap(({ run }) => {
        const { tag } = resolveRunExecution(run.summaryRow, run.detail, run.scenario);
        return tag === null ? [] : [tag];
      })
    : [];
  const { states: metadataStates, retry: retryMetadata } = useEvalImageMetadata(
    metadataTags,
    enabled && loadMetrics,
  );
  const retry = useCallback(() => {
    setRefreshKey((key) => key + 1);
    retryMetadata();
  }, [retryMetadata]);

  const states = [
    ...Object.values(loads).flatMap((load) => [load.detail, load.metrics, load.terms]),
    scenariosState,
  ];
  const termStates = runs.map((run) => loads[run.id]?.terms);
  const detailStates = runs.map((run) => loads[run.id]?.detail);
  // 병합은 모든 요청이 성공할 때가 아니라 끝날 때까지 기다린다. success terms 요청이 실패한 run 은
  // pooled 통계에서 제외하고 안내를 표시한다. 모든 병합 섹션을 비우지 않는다.
  // rules.md §7 은 화면을 막는 것보다 부분 렌더를 우선한다.
  const termsSettled =
    termStates.length > 0 &&
    termStates.every((state) => state?.status === "success" || state?.status === "error");
  const anyTermsLoaded = termStates.some((state) => state?.status === "success");
  const detailsSettled =
    detailStates.length > 0 &&
    detailStates.every((state) => state?.status === "success" || state?.status === "error");
  return {
    baseline,
    compareRuns,
    loads,
    metadataStates,
    retryMetadata,
    loading: states.some((state) => state.status === "loading"),
    loadFailed: states.some((state) => state.status === "error"),
    termsFailed: termStates.some((state) => state?.status === "error"),
    mergeReady: termsSettled && anyTermsLoaded && detailsSettled,
    retry,
  };
}
